"use client";

import { MicrocopyProvider } from "@lib/contentful/microcopy/MicrocopyContext";
import PreviewMode from "@components/cms/preview/PreviewMode";
import { GlobalModalProvider } from "@components/GlobalModalProvider/GlobalModalProvider";
import dynamic from "next/dynamic";
import type { ComponentType } from "react";
import { AnalyticsProvider } from "@lib/analytics/AnalyticsProvider";
import type { ISiteSettingsFields } from "@contentful-api/types/contentful";
import type { MicrocopySet } from "@lib/contentful/microcopy/types";
import type { Entry } from "contentful";
import { deserialize } from "@ungap/structured-clone";

const isProduction = process.env.NEXT_PUBLIC_ENVIRONMENT === "production";

let Backstage: ComponentType | null = null;
if (!isProduction) {
    Backstage = dynamic(() => import("@lib/monitoring/site-info-tooltip/Backstage"), {
        // For some reason setting this to false breaks SSR
        ssr: true,
    });
}

interface PageContentProps {
    children: React.ReactNode;
    pageType: string;
    siteSettings: Entry<ISiteSettingsFields>;
    sharedMicrocopySet: MicrocopySet;
    pageMicrocopySet?: MicrocopySet;
    isPreview?: boolean;
    microcopySets?: any;
    sizeGuideModal?: any;
}

export default function PageContent(props: PageContentProps) {
    const serializedFallback = [[2, []]];

    const {
        children,
        siteSettings,
        pageMicrocopySet,
        sharedMicrocopySet,
        microcopySets,
        pageType,
        isPreview = false,
    } = props;

    const {
        fields: {
            offerToLogIn,
            offerToCreateAnAccount,
            policiesAgreementTerms,
            authorizationInvitation,
            forgotYourPassword,
            contactFormAgreement,
            reCaptchaAgreement,
            shippingDetailsModal,
            profileLinkCollection,
            liveChatAgreement,
            liveChatOfflineDescription,
            giftWrappingDescription,
            eccoNewsletter,
            eccoMembership,
        } = {},
    } = siteSettings;

    const authorizationModules = {
        offerToLogIn,
        offerToCreateAnAccount,
        policiesAgreementTerms,
        authorizationInvitation,
        forgotYourPassword,
        eccoNewsletter,
        eccoMembership,
    };
    const contactFormModules = {
        contactFormAgreement,
        reCaptchaAgreement,
    };
    const liveChatModal = {
        liveChatAgreement,
        liveChatOfflineDescription,
    };

    return (
        <AnalyticsProvider pageType={pageType}>
            <MicrocopyProvider
                value={{
                    shared: sharedMicrocopySet,
                    page: pageMicrocopySet,
                    ...microcopySets,
                }}
            >
                <PreviewMode preview={isPreview} />
                <GlobalModalProvider
                    authorizationModules={authorizationModules}
                    linkCollection={profileLinkCollection}
                    sizeGuideModal={
                        siteSettings.fields?.sizeGuideModal || deserialize(serializedFallback)
                    }
                    contactFormModules={contactFormModules}
                    shippingDetailsModal={shippingDetailsModal}
                    liveChatModal={liveChatModal}
                    giftWrappingModal={giftWrappingDescription}
                >
                    {Backstage && <Backstage />}
                    {children}
                </GlobalModalProvider>
            </MicrocopyProvider>
        </AnalyticsProvider>
    );
}
